import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Eye, Loader2, BookOpen, GraduationCap, Shield, ArrowLeft } from "lucide-react";
import { useAuth, ROLE_HOME, type Role } from "@/lib/auth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ROLES: { role: Role; icon: typeof Shield; label: string }[] = [
  { role: "admin", icon: Shield, label: "Admin" },
  { role: "teacher", icon: BookOpen, label: "GV" },
  { role: "student", icon: GraduationCap, label: "HS" },
];

export function DemoBanner() {
  const { isDemo, demoRole, demoSignIn, logout } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = React.useState<Role | null>(null);

  if (!isDemo) return null;

  const handleSwitch = async (role: Role) => {
    if (role === demoRole || switching) return;
    setSwitching(role);
    try {
      await logout();
      await demoSignIn(role);
      navigate({ to: ROLE_HOME[role] });
    } catch {
      toast.error("Không thể chuyển vai trò");
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 pl-4 pr-1.5 py-1.5 rounded-full bg-amber-500 text-white text-[12px] font-medium shadow-[0_8px_30px_rgb(245,158,11,0.35)] ring-1 ring-amber-400/50 backdrop-blur-xl max-w-[calc(100vw-1rem)]">
      <Eye className="size-3.5 shrink-0" strokeWidth={1.5} />
      <span className="hidden sm:inline">Chế độ xem thử</span>

      <div className="flex gap-0.5 ml-1">
        {ROLES.map(({ role, icon: Icon, label }) => {
          const isActive = demoRole === role;
          const isLoading = switching === role;
          return (
            <button
              key={role}
              onClick={() => handleSwitch(role)}
              disabled={!!switching}
              className={cn(
                "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all duration-300",
                isActive
                  ? "bg-white text-amber-600 shadow-sm"
                  : "text-white/80 hover:text-white hover:bg-white/15",
                isLoading && "opacity-60",
              )}
            >
              {isLoading ? (
                <Loader2 className="size-3 animate-spin" strokeWidth={1.5} />
              ) : (
                <Icon className="size-3" strokeWidth={1.5} />
              )}
              {label}
            </button>
          );
        })}
      </div>

      <button
        onClick={async () => {
          await logout();
          window.location.href = "/";
        }}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white/15 text-white hover:bg-white/25 transition-colors ml-0.5"
        title="Về trang chủ"
      >
        <ArrowLeft className="size-3" strokeWidth={1.5} />
        <span className="hidden md:inline">Thoát</span>
      </button>
    </div>
  );
}
